import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { useTranslation } from '../../../app/LanguageContext';
import { useWorkspace } from '../../../app/WorkspaceContext';
import { getDashboardOverview } from '../../../app/api';
import KpiGrid from '../private/dashboard/KpiGrid';
import OrdersStatusChart from '../private/dashboard/OrdersStatusChart';
import MediaTypeChart from '../private/dashboard/MediaTypeChart';
import RecentOrdersPanel from '../private/dashboard/RecentOrdersPanel';
import TopProductsPanel from '../private/dashboard/TopProductsPanel';
import AlertsPanel from '../private/dashboard/AlertsPanel';
import DashboardSkeleton from '../private/dashboard/DashboardSkeleton';
import DashboardErrorState from '../private/dashboard/DashboardErrorState';

const POLL_MS = 10000;

const HomePage = () => {
  const { translate, language } = useTranslation();
  const { loading: workspaceLoading, currentWorkspace } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [, setTick] = useState(0);
  const inFlightRef = useRef(false);

  const dateLocale = language === 'en-US' ? enUS : ptBR;

  const load = useCallback(
    async (isRefresh) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      if (!isRefresh) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await getDashboardOverview();
        if (res && res.success === false) {
          throw new Error(res.error || res.message || translate('home.dashboard.load_error'));
        }
        setPayload(res?.data || null);
        setError(null);
        setLastUpdatedAt(Date.now());
      } catch (e) {
        const msg = e?.message || translate('home.dashboard.load_error');
        if (isRefresh) {
          return;
        }
        setError(msg);
        setPayload(null);
        setLastUpdatedAt(null);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
    },
    [translate],
  );

  useEffect(() => {
    if (workspaceLoading) {
      return;
    }
    if (!currentWorkspace) {
      inFlightRef.current = false;
      setLoading(false);
      setError(null);
      setPayload(null);
      setLastUpdatedAt(null);
      return;
    }
    inFlightRef.current = false;
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentWorkspace?.id
  }, [workspaceLoading, currentWorkspace?.id, load]);

  useEffect(() => {
    if (workspaceLoading || !currentWorkspace) {
      return undefined;
    }
    const id = window.setInterval(() => {
      load(true);
    }, POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentWorkspace?.id
  }, [workspaceLoading, currentWorkspace?.id, load]);

  useEffect(() => {
    if (lastUpdatedAt == null) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [lastUpdatedAt]);

  const kpis = payload?.kpis;
  const charts = payload?.charts;
  const lists = payload?.lists;
  const alerts = payload?.alerts;

  const isEmptyWorkspace =
    payload &&
    !kpis?.totalProducts &&
    !kpis?.totalSkus &&
    !kpis?.totalOrders &&
    !kpis?.totalCustomers &&
    !kpis?.totalMedias;

  const updatedLine =
    lastUpdatedAt != null
      ? `${translate('home.dashboard.updated_status_prefix')}${formatDistanceToNow(lastUpdatedAt, {
          addSuffix: true,
          locale: dateLocale,
          includeSeconds: true,
        })}`
      : null;

  if (workspaceLoading || !currentWorkspace) {
    return (
      <Box>
        <DashboardSkeleton />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <DashboardSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <DashboardErrorState message={error} onRetry={() => load(false)} translate={translate} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {updatedLine ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            fontSize: '0.65rem',
            lineHeight: 1.2,
            opacity: 0.72,
            pointerEvents: 'none',
            maxWidth: '55%',
            textAlign: 'right',
          }}
        >
          {updatedLine}
        </Typography>
      ) : null}

      {isEmptyWorkspace ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('home.dashboard.no_data')}
        </Typography>
      ) : null}

      <AlertsPanel alerts={alerts} translate={translate} />

      <Box>
        <KpiGrid kpis={kpis} translate={translate} />

        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <OrdersStatusChart
              data={charts?.ordersByStatus}
              title={translate('home.dashboard.chart_orders_status')}
              emptyLabel={translate('home.dashboard.empty_chart')}
            />
            <MediaTypeChart
              data={charts?.mediaByMimeType}
              title={translate('home.dashboard.chart_media_mime')}
              emptyLabel={translate('home.dashboard.empty_chart')}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <RecentOrdersPanel orders={lists?.recent_orders} translate={translate} />
            <TopProductsPanel products={lists?.top_products} translate={translate} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HomePage;
